import type { InfiniteData, QueryClient, QueryKey } from '@tanstack/react-query';
import { type endpoints, type EndpointKey, type EndpointResponse, type Page } from '@missions/types';
import type { ApiClient, CallArgs, CallInput } from './client';

/* ============================================================================
   TanStack Query helpers.
     useQuery(queryOptionsFor(api, 'projects.get', { params: { id } }))
     useInfiniteQuery(infiniteOptionsFor(api, 'missions.list', { query: { projectId } }))
   Keys: ['missions', <endpointKey>, <params>, <query>] so invalidation can target
   an endpoint (`keyFor('missions.list')`) or one call.
   ========================================================================== */

export const KEY_ROOT = 'missions' as const;

type LooseInput = { params?: unknown; query?: unknown };

/** Query key for an endpoint; pass input to target one call, omit to match all calls of that endpoint. */
export function keyFor<K extends EndpointKey>(key: K, input?: LooseInput): QueryKey {
  if (!input) return [KEY_ROOT, key];
  return [KEY_ROOT, key, input.params ?? null, input.query ?? null];
}

/** Query keys grouped by domain prefix, e.g. invalidate everything under 'missions.' after a bulk op. */
export const keyForDomain = (prefix: string): QueryKey => [KEY_ROOT, prefix];
export const matchesDomain = (prefix: string) => (q: { queryKey: QueryKey }) =>
  q.queryKey[0] === KEY_ROOT && typeof q.queryKey[1] === 'string' && q.queryKey[1].startsWith(prefix);

export function queryOptionsFor<K extends EndpointKey>(api: ApiClient, key: K, ...args: CallArgs<K>) {
  const input = args[0] as LooseInput | undefined;
  return {
    queryKey: keyFor(key, input ?? {}),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      api.call(key, ...([{ ...(input ?? {}), signal }] as unknown as CallArgs<K>)),
  };
}

type PaginatedKey = {
  [K in EndpointKey]: (typeof endpoints)[K] extends { paginated: true } ? K : never;
}[EndpointKey];

type ItemOf<K extends PaginatedKey> = EndpointResponse<K> extends Page<infer T> ? T : never;

/** Cursor pagination → useInfiniteQuery options ("Load 25 more", infinite scroll). */
export function infiniteOptionsFor<K extends PaginatedKey>(
  api: ApiClient,
  key: K,
  input: Omit<CallInput<K>, 'signal'>,
) {
  const i = input as LooseInput & { query?: Record<string, unknown> };
  return {
    queryKey: [...keyFor(key, i), 'infinite'] as QueryKey,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }: { pageParam: string | undefined; signal: AbortSignal }) =>
      api.call(
        key,
        ...([{ ...i, query: { ...(i.query ?? {}), cursor: pageParam }, signal }] as unknown as CallArgs<K>),
      ) as Promise<Page<ItemOf<K>>>,
    getNextPageParam: (last: Page<ItemOf<K>>) => (last.page.hasMore ? (last.page.nextCursor ?? undefined) : undefined),
  };
}

/** Flattens infinite pages into one array. */
export const flattenPages = <T>(data: InfiniteData<Page<T>> | undefined): T[] =>
  data?.pages.flatMap((p) => p.data) ?? [];

/* ---------- Optimistic updates ---------- */

export interface OptimisticContext {
  snapshots: Array<[QueryKey, unknown]>;
}

/**
 * Builds onMutate/onError/onSettled for useMutation. `update` patches every cached query matching `keys`
 * (exact keys or endpoint prefixes); errors roll back; settled re-validates.
 *
 *   useMutation({ mutationFn, ...optimistic(qc, [keyFor('missions.list')], (old, vars) => patchItem(old, vars.id, vars.patch)) })
 */
export function optimistic<V>(
  qc: QueryClient,
  keys: QueryKey[],
  update: (old: unknown, vars: V) => unknown,
): {
  onMutate: (vars: V) => Promise<OptimisticContext>;
  onError: (_e: unknown, _v: V, ctx: OptimisticContext | undefined) => void;
  onSettled: () => Promise<void>;
} {
  return {
    onMutate: async (vars) => {
      await Promise.all(keys.map((k) => qc.cancelQueries({ queryKey: k })));
      const snapshots: Array<[QueryKey, unknown]> = [];
      for (const k of keys) {
        for (const [qk, data] of qc.getQueriesData({ queryKey: k })) {
          snapshots.push([qk, data]);
          qc.setQueryData<unknown>(qk, () => update(data, vars));
        }
      }
      return { snapshots };
    },
    onError: (_e, _v, ctx) => {
      ctx?.snapshots.forEach(([k, d]) => qc.setQueryData<unknown>(k, () => d));
    },
    onSettled: async () => {
      await Promise.all(keys.map((k) => qc.invalidateQueries({ queryKey: k })));
    },
  };
}

/** Patch one item (by id) inside a Page, an InfiniteData<Page> or a `{ data: [] }` list. */
export function patchItem<T extends { id: string }>(
  old: unknown,
  id: string,
  patch: Partial<T> | ((x: T) => T),
): unknown {
  const apply = (x: T): T => (x.id !== id ? x : typeof patch === 'function' ? patch(x) : { ...x, ...patch });
  if (!old || typeof old !== 'object') return old;
  const o = old as { pages?: Array<{ data: T[] }>; data?: T[] };
  if (Array.isArray(o.pages)) return { ...o, pages: o.pages.map((p) => ({ ...p, data: p.data.map(apply) })) };
  if (Array.isArray(o.data)) return { ...o, data: o.data.map(apply) };
  if ('id' in (old as object)) return apply(old as T);
  return old;
}

/** Remove one item (by id) from a Page / InfiniteData / list. */
export function removeItem(old: unknown, id: string): unknown {
  if (!old || typeof old !== 'object') return old;
  const keep = (x: { id: string }) => x.id !== id;
  const o = old as { pages?: Array<{ data: Array<{ id: string }> }>; data?: Array<{ id: string }> };
  if (Array.isArray(o.pages)) return { ...o, pages: o.pages.map((p) => ({ ...p, data: p.data.filter(keep) })) };
  if (Array.isArray(o.data)) return { ...o, data: o.data.filter(keep) };
  return old;
}
